import { useEffect, useMemo, useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/use-toast";
import {
  forceReleaseLockedFunds,
  getLockedFundsDetails,
  type LockedFundsDetails,
} from "../services/adminAccountService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import * as adminUserService from "../services/adminUserService";
import type { SimulationClientSummary } from "../services/adminUserService";
import { useTranslation } from "react-i18next";

const fieldClass =
  "bg-background text-foreground placeholder:text-muted-foreground border border-input dark:bg-white/5 dark:text-white dark:placeholder:text-white/50";
const selectTriggerClass =
  "bg-background text-foreground border-input dark:bg-white/5 dark:text-white";

const AdminMaintenancePage = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    userId: "",
    currency: "USD",
    amount: "",
    referenceId: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState<SimulationClientSummary[]>([]);
  const [search, setSearch] = useState("");
  const [loadingClients, setLoadingClients] = useState(false);
  const [locks, setLocks] = useState<LockedFundsDetails | null>(null);
  const [locksLoading, setLocksLoading] = useState(false);

  useEffect(() => {
    const loadClients = async () => {
      setLoadingClients(true);
      try {
        const data = await adminUserService.listSimulationClients();
        setClients(data);
      } catch (error) {
        const message =
          typeof error === "string"
            ? error
            : error instanceof Error
            ? error.message
            : t("admin.maintenance.errors.loadClients");
        toast({
          title: t("errors.generic"),
          description: message,
          variant: "destructive",
        });
      } finally {
        setLoadingClients(false);
      }
    };
    void loadClients();
  }, [toast, t]);

  const filteredClients = useMemo(() => {
    if (!search.trim()) {
      return clients;
    }
    const term = search.toLowerCase();
    return clients.filter(
      (client) =>
        client.firstName.toLowerCase().includes(term) ||
        client.lastName.toLowerCase().includes(term) ||
        client.email.toLowerCase().includes(term)
    );
  }, [clients, search]);

  useEffect(() => {
    const fetchLocks = async () => {
      if (!form.userId) {
        setLocks(null);
        return;
      }
      setLocksLoading(true);
      try {
        const details = await getLockedFundsDetails(Number(form.userId));
        setLocks(details);
      } catch (error) {
        setLocks(null);
        const message =
          typeof error === "string"
            ? error
            : error instanceof Error
            ? error.message
            : t(
                "admin.maintenance.errors.loadDetails",
                "Impossible de recuperer les details."
              );
        toast({
          title: t("errors.generic"),
          description: message,
          variant: "destructive",
        });
      } finally {
        setLocksLoading(false);
      }
    };
    void fetchLocks();
  }, [form.userId, toast, t]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.userId || !form.currency || !form.amount) {
      toast({
        title: t("admin.maintenance.requiredTitle"),
        description:
          t(
            "admin.maintenance.requiredDescription",
            "Merci de renseigner l'utilisateur, la devise et le montant."
          ),
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      await forceReleaseLockedFunds({
        userId: Number(form.userId),
        currency: form.currency.toUpperCase(),
        amount: Number(form.amount),
        referenceId: form.referenceId ? Number(form.referenceId) : undefined,
      });
      toast({
        title: t("admin.maintenance.toasts.successTitle"),
        description: t(
          "admin.maintenance.toasts.successDescription",
          "Le montant verrouille a ete libere avec succes."
        ),
      });
      setForm({
        userId: "",
        currency: "USD",
        amount: "",
        referenceId: "",
      });
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : error instanceof Error
          ? error.message
          : t(
              "admin.maintenance.errors.releaseFunds",
              "Impossible de liberer les fonds."
            );
      toast({
        title: t("errors.generic"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground dark:text-white">
          {t("admin.maintenance.title")}
        </h1>
        <p className="text-sm text-muted-foreground dark:text-white/70">
          {t(
            "admin.maintenance.subtitle",
            "Outil de secours pour debloquer manuellement un montant verrouille sur le compte trading d'un utilisateur."
          )}
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-muted/30 p-6 dark:border-white/10 dark:bg-white/5">
        <div className="space-y-2">
          <Label className="text-foreground dark:text-white/80">
            {t("admin.maintenance.searchClient")}
          </Label>
          <Input
            type="text"
            placeholder={t("admin.maintenance.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={fieldClass}
          />
        </div>
        <div className="mt-4 space-y-2">
          <Label className="text-foreground dark:text-white/80">
            {t("admin.maintenance.selectClient")}
          </Label>
          <Select
            disabled={loadingClients || filteredClients.length === 0}
            value={form.userId}
            onValueChange={(value) =>
              setForm((prev) => ({ ...prev, userId: value }))
            }>
            {form.userId && (
              <div className="rounded-2xl border border-border bg-muted/20 p-6 dark:border-white/10 dark:bg-white/5">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-foreground dark:text-white">
                    {t("admin.maintenance.lockedFundsDetails")}
                  </h2>
                  {locksLoading && (
                    <span className="text-xs text-muted-foreground dark:text-white/60">
                      {t("common.loading")}
                    </span>
                  )}
                </div>
                {locks ? (
                  <div className="mt-4 space-y-4 text-sm text-foreground/90 dark:text-white/80">
                    <div>
                      <p className="text-xs uppercase text-muted-foreground dark:text-white/60">
                        {t("admin.maintenance.tradingAccounts")}
                      </p>
                      <div className="mt-2 grid gap-3 md:grid-cols-2">
                        {locks.tradingAccounts.map((account) => (
                          <div
                            key={account.id}
                            className="rounded-xl border border-border bg-muted/50 p-3 dark:border-white/10 dark:bg-black/20">
                            <p className="font-medium text-foreground dark:text-white">
                              {account.currency}
                            </p>
                            <p className="text-xs text-muted-foreground dark:text-white/60">
                              {t("admin.maintenance.balance")}: {account.balance.toFixed(2)}{" "}
                              {account.currency}
                            </p>
                            <p className="text-xs text-rose-700 dark:text-rose-300">
                              {t("admin.maintenance.locked")}: {account.lockedBalance.toFixed(2)}{" "}
                              {account.currency}
                            </p>
                          </div>
                        ))}
                        {locks.tradingAccounts.length === 0 && (
                          <p className="text-xs text-muted-foreground dark:text-white/60">
                            {t("admin.maintenance.noTradingAccount")}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground dark:text-white/60">
                        {t("admin.maintenance.pendingLimitOrders")}
                      </p>
                      {locks.pendingLimitOrders.length === 0 ? (
                        <p className="mt-2 text-xs text-muted-foreground dark:text-white/60">
                          {t("admin.maintenance.noPendingLimitOrders")}
                        </p>
                      ) : (
                        <div className="mt-2 space-y-2">
                          {locks.pendingLimitOrders.map((order) => (
                            <div
                              key={order.id}
                              className="rounded-xl border border-border bg-muted/50 p-3 dark:border-white/10 dark:bg-black/20">
                              <div className="flex items-center justify-between text-foreground dark:text-white">
                                <span className="font-medium">
                                  {order.symbol}
                                </span>
                                <span
                                  className={`text-xs ${
                                    order.side === "BUY"
                                      ? "text-emerald-700 dark:text-emerald-300"
                                      : "text-rose-700 dark:text-rose-300"
                                  }`}>
                                  {order.side}
                                </span>
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground dark:text-white/60">
                                <p>
                                  {t("admin.maintenance.quantity")}: {order.quantity} | {t("admin.maintenance.limitPrice")}:{" "}
                                  {order.price
                                    ? `$${order.price.toFixed(2)}`
                                    : "-"}
                                </p>
                                {order.lockedAmount > 0 && (
                                  <p className="text-rose-700 dark:text-rose-300">
                                    {t("admin.maintenance.locked")}: ${order.lockedAmount.toFixed(2)}
                                  </p>
                                )}
                                <p>
                                  {t("admin.maintenance.createdAt")}:{" "}
                                  {new Date(order.createdAt).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  !locksLoading && (
                    <p className="mt-2 text-sm text-muted-foreground dark:text-white/70">
                      {t("admin.maintenance.selectClientToSeeDetails")}
                    </p>
                  )
                )}
              </div>
            )}

            <SelectTrigger className={selectTriggerClass}>
              <SelectValue
                placeholder={
                  loadingClients
                    ? t("common.loading")
                    : filteredClients.length === 0
                    ? t("admin.maintenance.noClientMatch")
                    : t("admin.maintenance.chooseClient")
                }
              />
            </SelectTrigger>
            <SelectContent>
              {filteredClients.map((client) => (
                <SelectItem key={client.id} value={client.id.toString()}>
                  {client.firstName} {client.lastName} ({client.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-4 rounded-2xl border border-border bg-muted/30 p-6 dark:border-white/10 dark:bg-white/5">
        <div className="space-y-2">
          <Label htmlFor="userId" className="text-foreground dark:text-white/80">
            {t("admin.maintenance.userId")} *
          </Label>
          <Input
            id="userId"
            name="userId"
            type="number"
            min="1"
            value={form.userId}
            onChange={handleChange}
            className={fieldClass}
            placeholder={t("admin.maintenance.userIdPlaceholder")}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="currency" className="text-foreground dark:text-white/80">
            {t("admin.maintenance.currency")} *
          </Label>
          <Input
            id="currency"
            name="currency"
            type="text"
            value={form.currency}
            onChange={handleChange}
            className={`${fieldClass} uppercase`}
            placeholder="USD"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="amount" className="text-foreground dark:text-white/80">
            {t("admin.maintenance.amountToRelease")} *
          </Label>
          <Input
            id="amount"
            name="amount"
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={handleChange}
            className={fieldClass}
            placeholder={t("admin.maintenance.amountPlaceholder")}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="referenceId" className="text-foreground dark:text-white/80">
            {t("admin.maintenance.referenceOptional")}
          </Label>
          <Input
            id="referenceId"
            name="referenceId"
            type="number"
            min="0"
            value={form.referenceId}
            onChange={handleChange}
            className={fieldClass}
            placeholder={t(
              "admin.maintenance.referencePlaceholder",
              "ID d'ordre ou de transaction"
            )}
          />
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-rose-500 text-white hover:bg-rose-600">
          {submitting
            ? t("admin.maintenance.releasing")
            : t("admin.maintenance.releaseButton")}
        </Button>
      </form>
    </div>
  );
};

export default AdminMaintenancePage;
